<template>
  <GlobalBackgroundWrapper page-key="my-reservations" settings-position="top-right">
    <div class="reservations-container">
      <el-card>
        <div slot="header">
          <h3>我的预约记录</h3>
        </div>

        <!-- 桌面端表格视图 -->
        <el-table
            :data="sortedReservations"
            v-loading="loading"
            v-if="!isMobile"
            :default-sort="{prop: 'reservationDate', order: 'descending'}"
            @sort-change="handleSortChange"
        >
          <el-table-column
              prop="reservationDate"
              label="预约日期"
              width="120"
              sortable="custom"
              :sort-orders="['ascending', 'descending']"
          >
            <template slot-scope="scope">
              {{ formatDate(scope.row.reservationDate) }}
            </template>
          </el-table-column>
          <el-table-column
              prop="timeSlot"
              label="时段"
              width="120"
              sortable="custom"
              :sort-orders="['ascending', 'descending']"
          >
            <template slot-scope="scope">
              {{ getTimeSlotLabel(scope.row.timeSlot) }}
            </template>
          </el-table-column>
          <el-table-column
              label="教练"
              width="120"
              sortable="custom"
              :sort-orders="['ascending', 'descending']"
              :sort-by="getCoachNameForSort"
          >
            <template slot-scope="scope">
              {{ getCoachName(scope.row.coach) }}
            </template>
          </el-table-column>
          <el-table-column
              prop="subject"
              label="科目"
              width="100"
              sortable="custom"
              :sort-orders="['ascending', 'descending']"
          >
            <template slot-scope="scope">
              {{ getSubjectText(scope.row.subject) }}
            </template>
          </el-table-column>
          <el-table-column
              prop="status"
              label="状态"
              width="100"
              sortable
              :sort-orders="['ascending', 'descending']"
          >
            <template slot-scope="scope">
              <el-tag :type="getStatusType(scope.row.status)">
                {{ getStatusText(scope.row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="createdAt" label="预约时间" width="180">
            <template slot-scope="scope">
              {{ formatDateTime(scope.row.createdAt) }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="180">
            <template slot-scope="scope">
              <el-button
                  v-if="scope.row.status === 'PENDING'"
                  type="text"
                  size="small"
                  @click="cancelReservation(scope.row.reservationId)"
              >
                取消
              </el-button>
              <el-button
                  v-if="canDelete(scope.row.status)"
                  type="text"
                  size="small"
                  style="color: #f56c6c;"
                  @click="deleteReservation(scope.row.reservationId)"
              >
                删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 移动端卡片视图 -->
        <div v-if="isMobile" class="mobile-reservations-list" v-loading="loading">
          <!-- 移动端排序控件 -->
          <div class="mobile-sort-controls">
            <el-select
                v-model="mobileSortField"
                placeholder="选择排序字段"
                size="small"
                @change="handleMobileSortChange"
            >
              <el-option label="预约日期" value="reservationDate"></el-option>
              <el-option label="教练" value="coach"></el-option>
              <el-option label="预约时段" value="timeSlot"></el-option>
              <el-option label="科目" value="subject"></el-option>
              <el-option label="状态" value="status"></el-option>
            </el-select>
            <el-radio-group v-model="mobileSortOrder" size="small" @change="handleMobileSortChange">
              <el-radio-button label="asc">升序</el-radio-button>
              <el-radio-button label="desc">降序</el-radio-button>
            </el-radio-group>
          </div>

          <div v-for="reservation in sortedReservations" :key="reservation.reservationId" class="reservation-card">
            <el-card shadow="hover">
              <div class="card-header">
                <div class="reservation-main">
                  <h4>{{ getCoachName(reservation.coach) }}</h4>
                  <el-tag :type="getStatusType(reservation.status)" size="mini">
                    {{ getStatusText(reservation.status) }}
                  </el-tag>
                </div>
                <div class="reservation-date">
                  {{ formatDate(reservation.reservationDate) }}
                </div>
              </div>

              <div class="card-content">
                <div class="info-row">
                  <span class="label">时段:</span>
                  <span class="value">{{ getTimeSlotLabel(reservation.timeSlot) }}</span>
                </div>

                <div class="info-row">
                  <span class="label">科目:</span>
                  <span class="value">{{ getSubjectText(reservation.subject) }}</span>
                </div>

                <div class="info-row">
                  <span class="label">预约时间:</span>
                  <span class="value">{{ formatDateTime(reservation.createdAt) }}</span>
                </div>
              </div>

              <div class="card-actions">
                <div class="action-buttons">
                  <el-button
                      v-if="reservation.status === 'PENDING'"
                      type="danger"
                      size="small"
                      @click="cancelReservation(reservation.reservationId)"
                  >
                    取消预约
                  </el-button>
                  <el-button
                      v-if="canDelete(reservation.status)"
                      type="text"
                      size="small"
                      style="color: #f56c6c;"
                      @click="deleteReservation(reservation.reservationId)"
                  >
                    删除记录
                  </el-button>
                  <div v-if="!canDelete(reservation.status) && reservation.status !== 'PENDING'" class="no-action">
                    无法操作
                  </div>
                </div>
              </div>
            </el-card>
          </div>
        </div>
      </el-card>
    </div>
  </GlobalBackgroundWrapper>
</template>

<script>
import { reservationAPI } from '@/services/api'
import GlobalBackgroundWrapper from '@/components/GlobalBackgroundWrapper.vue'

export default {
  name: 'MyReservations',
  components: { GlobalBackgroundWrapper },
  data() {
    return {
      loading: false,
      reservations: [],
      isMobile: false,
      // 排序相关数据
      sortField: 'reservationDate',
      sortOrder: 'descending',
      // 移动端排序数据
      mobileSortField: 'reservationDate',
      mobileSortOrder: 'desc'
    }
  },
  computed: {
    // 计算排序后的预约记录
    sortedReservations() {
      if (this.reservations.length === 0) return []

      let field, order

      // 根据设备类型获取排序参数
      if (this.isMobile) {
        field = this.mobileSortField
        order = this.mobileSortOrder
      } else {
        field = this.sortField
        // 将Element UI的排序值转换为通用排序值
        order = this.sortOrder === 'ascending' ? 'asc' : 'desc'
      }

      return this.sortReservations(field, order)
    }
  },
  async mounted() {
    this.checkIsMobile()
    window.addEventListener('resize', this.checkIsMobile)
    await this.loadReservations()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkIsMobile)
  },
  methods: {
    checkIsMobile() {
      this.isMobile = window.innerWidth <= 768
    },

    async loadReservations() {
      this.loading = true
      try {
        const response = await reservationAPI.getMyReservations()
        console.log('预约数据:', response.data) // 调试用
        this.reservations = response.data || []
      } catch (error) {
        console.error('加载预约记录失败:', error)
        this.showMessage('加载预约记录失败', 'error')
      } finally {
        this.loading = false
      }
    },

    // 处理表格排序变化
    handleSortChange({ prop, order }) {
      this.sortField = prop
      this.sortOrder = order || 'descending'
    },

    // 处理移动端排序变化
    handleMobileSortChange() {
      // 移动端排序变化时无需额外处理，计算属性会自动更新
    },

    // 排序方法
    sortReservations(field, order) {
      const sorted = [...this.reservations]

      sorted.sort((a, b) => {
        let aValue, bValue

        // 根据字段获取值
        switch (field) {
          case 'reservationDate':
            aValue = new Date(a.reservationDate)
            bValue = new Date(b.reservationDate)
            break
          case 'coach':
            aValue = this.getCoachNameForSort(a.coach)
            bValue = this.getCoachNameForSort(b.coach)
            break
          case 'timeSlot':
            // 将时段转换为可排序的值
            aValue = this.getTimeSlotOrder(a.timeSlot)
            bValue = this.getTimeSlotOrder(b.timeSlot)
            break
          case 'subject':
            // 将科目转换为可排序的值
            aValue = this.getSubjectOrder(a.subject)
            bValue = this.getSubjectOrder(b.subject)
            break
          case 'status':
            // 将状态转换为可排序的值
            aValue = this.getStatusOrder(a.status)
            bValue = this.getStatusOrder(b.status)
            break
          default:
            aValue = a[field]
            bValue = b[field]
        }

        // 处理空值情况
        if (aValue === null || aValue === undefined) aValue = ''
        if (bValue === null || bValue === undefined) bValue = ''

        // 比较逻辑
        let result = 0
        if (aValue < bValue) result = -1
        if (aValue > bValue) result = 1

        // 根据排序方向调整结果
        const isDesc = order === 'desc' || order === 'descending'
        return isDesc ? -result : result
      })

      return sorted
    },

    // 获取教练姓名用于排序
    getCoachNameForSort(coach) {
      if (!coach) return ''
      return coach.user?.realName || coach.realName || ''
    },

    // 获取时段排序值
    getTimeSlotOrder(slot) {
      const slotOrder = {
        '08:00-10:00': 1,
        '10:00-12:00': 2,
        '14:00-16:00': 3,
        '16:00-18:00': 4
      }
      return slotOrder[slot] || 99
    },

    // 获取科目排序值
    getSubjectOrder(subject) {
      const subjectOrder = {
        'SUBJECT2': 2,
        'SUBJECT3': 3,
        'subject2': 2,
        'subject3': 3
      }
      return subjectOrder[subject] || 99
    },

    // 获取状态排序值
    getStatusOrder(status) {
      const statusOrder = {
        'PENDING': 1,
        'CONFIRMED': 2,
        'COMPLETED': 3,
        'CANCELLED': 4,
        'REJECTED': 5
      }
      return statusOrder[status] || 99
    },

    async cancelReservation(id) {
      try {
        await this.showConfirm('确定要取消这个预约吗？', '提示', {
          type: 'warning'
        })

        await reservationAPI.cancel(id)
        this.showMessage('取消成功', 'success')
        await this.loadReservations()
      } catch (error) {
        if (error !== 'cancel') {
          this.showMessage('取消失败: ' + (error.message || '未知错误'), 'error')
        }
      }
    },

    async deleteReservation(id) {
      try {
        await this.showConfirm('确定要删除这条预约记录吗？删除后无法恢复。', '警告', {
          type: 'warning',
          confirmButtonText: '确定删除',
          cancelButtonText: '再想想',
          confirmButtonClass: 'el-button--danger'
        })

        // 调用后端删除API
        await reservationAPI.delete(id)

        this.showMessage('删除成功', 'success')
        // 重新加载数据，确保与服务器同步
        await this.loadReservations()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error)
          this.showMessage('删除失败: ' + (error.message || '未知错误'), 'error')
        }
      }
    },

    // 响应式消息提示
    showMessage(message, type = 'info') {
      const position = this.isMobile ? 'top' : 'top-right'
      this.$message({
        message,
        type,
        position,
        duration: 3000,
        showClose: true
      })
    },

    // 响应式确认框
    showConfirm(message, title, options = {}) {
      const defaultOptions = {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
        // 移动端居中显示，桌面端位置不变
        customClass: this.isMobile ? 'mobile-confirm-dialog' : ''
      }

      return this.$confirm(message, title, {
        ...defaultOptions,
        ...options
      })
    },

    // 判断是否可以删除记录
    canDelete(status) {
      // 允许删除已取消、已完成、已拒绝的记录
      const deletableStatus = ['CANCELLED', 'COMPLETED', 'REJECTED']
      return deletableStatus.includes(status)
    },

    getCoachName(coach) {
      if (!coach) return '未知教练'
      // 尝试从不同路径获取教练姓名
      return coach.user?.realName || coach.realName || '未知教练'
    },

    getTimeSlotLabel(slot) {
      const slots = {
        '08:00-10:00': '上午 (08:00-10:00)',
        '10:00-12:00': '上午 (10:00-12:00)',
        '14:00-16:00': '下午 (14:00-16:00)',
        '16:00-18:00': '下午 (16:00-18:00)'
      }
      return slots[slot] || slot
    },

    getSubjectText(subject) {
      const subjects = {
        'SUBJECT2': '科目二',
        'SUBJECT3': '科目三',
        'subject2': '科目二',
        'subject3': '科目三'
      }
      return subjects[subject] || subject
    },

    getStatusType(status) {
      const types = {
        'PENDING': 'warning',
        'CONFIRMED': 'success',
        'COMPLETED': 'info',
        'CANCELLED': 'danger',
        'REJECTED': 'danger'
      }
      return types[status] || 'info'
    },

    getStatusText(status) {
      const texts = {
        'PENDING': '待确认',
        'CONFIRMED': '已确认',
        'COMPLETED': '已完成',
        'CANCELLED': '已取消',
        'REJECTED': '已拒绝'
      }
      return texts[status] || status
    },

    formatDate(date) {
      if (!date) return '无效日期'
      try {
        // 处理 LocalDate 格式 (yyyy-MM-dd)
        if (typeof date === 'string' && date.includes('T')) {
          return new Date(date).toLocaleDateString()
        }
        return new Date(date + 'T00:00:00').toLocaleDateString()
      } catch (e) {
        console.error('日期格式化错误:', e, date)
        return '无效日期'
      }
    },

    formatDateTime(datetime) {
      if (!datetime) return '无效时间'
      try {
        return new Date(datetime).toLocaleString()
      } catch (e) {
        console.error('时间格式化错误:', e, datetime)
        return '无效时间'
      }
    }
  }
}
</script>

<style scoped>
.reservations-container {
  padding: 20px;
  min-height: 100vh;
  position: relative;
  z-index: 1;
}

/* 移动端排序控件样式 */
.mobile-sort-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

@media (max-width: 480px) {
  .mobile-sort-controls {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .mobile-sort-controls .el-select,
  .mobile-sort-controls .el-radio-group {
    width: 100%;
  }

  .mobile-sort-controls .el-radio-group {
    display: flex;
    justify-content: space-between;
  }

  .mobile-sort-controls .el-radio-button {
    flex: 1;
  }
}

/* 移动端卡片样式 */
.mobile-reservations-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.reservation-card {
  width: 100%;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.reservation-main {
  display: flex;
  align-items: center;
  gap: 8px;
}

.reservation-main h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.reservation-date {
  color: #909399;
  font-size: 14px;
}

.card-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.info-row .label {
  color: #606266;
  font-size: 14px;
  min-width: 80px;
}

.info-row .value {
  color: #303133;
  font-size: 14px;
  text-align: right;
  flex: 1;
}

.card-actions {
  text-align: center;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.no-action {
  color: #909399;
  font-size: 14px;
  font-style: italic;
  text-align: center;
  padding: 8px 0;
}

/* 卡片毛玻璃效果 */
.el-card {
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border-radius: 16px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.el-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);
}

/* 移动端优化 */
@media (max-width: 768px) {
  .reservations-container {
    padding: 10px;
  }

  .el-card {
    border-radius: 12px;
  }

  .reservation-main h4 {
    font-size: 15px;
  }

  .reservation-date {
    font-size: 13px;
  }

  .info-row .label,
  .info-row .value {
    font-size: 13px;
  }

  .card-actions .el-button {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .reservation-main h4 {
    font-size: 14px;
  }

  .info-row .label,
  .info-row .value {
    font-size: 12px;
  }
}

/* 表格排序样式优化 */
:deep(.el-table .caret-wrapper) {
  height: 29px;
}

:deep(.el-table .sort-caret.ascending) {
  top: 3px;
}

:deep(.el-table .sort-caret.descending) {
  bottom: 3px;
}
</style>

<style>
/* 全局样式 - 移动端确认对话框优化 */
.mobile-confirm-dialog {
  width: 90% !important;
  max-width: 320px !important;
  margin: 0 auto !important;
}

.mobile-confirm-dialog .el-message-box__content {
  padding: 15px 20px !important;
}

.mobile-confirm-dialog .el-message-box__btns {
  padding: 10px 20px 15px !important;
}

/* 移动端消息提示位置优化 */
@media (max-width: 768px) {
  .el-message {
    min-width: 90% !important;
    width: auto !important;
    left: 50% !important;
    transform: translateX(-50%) !important;
  }
}

/* 桌面端消息提示位置优化 */
@media (min-width: 769px) {
  .el-message {
    min-width: 380px !important;
  }
}

/* 背景设置抽屉样式 */
.background-settings-drawer {
  width: 320px !important;
}

@media (max-width: 768px) {
  .background-settings-drawer {
    width: 85% !important;
  }
}
</style>
